import React, { useMemo } from 'react';
import {
  Form,
  Input,
  Radio,
  Select,
  FormInstance,
  ColorPicker,
  InputNumber,
} from 'antd';
import type { Color } from 'antd/es/color-picker';
import { useQuery } from '@tanstack/react-query';
import UploadImage from '@/components/UploadImage';
import { BenefitFormData, BenefitType } from '../types';
import { FORM_DEFAULT_VALUES, QUERY_KEYS } from '../constants';
import { getBenefitTypeText } from '../utils';
import { getProductStockList } from '@/api/product';
import { getBenefitTypeList } from '../api';
import {
  extractProductRecords,
  filterAvatarFrameOptions,
} from '../utils/dataUtils';
import AvatarFrameSelector from './AvatarFrameSelector';
import AvatarFrameSelect from './AvatarFrameSelect';
import { 
  formatGradientToHexLimited, 
  parseGradientString, 
  getDefaultGradient,
  limitGradientToTwoColors,
  convertColorPairToGradient,
  isColorPairFormat,
} from '../utils/gradientUtils';

interface BenefitFormProps {
  form: FormInstance<BenefitFormData>;
}

const UPLOAD_IMAGE_SIZE = 400;
const QUERY_PAGE_SIZE = 20;

const BenefitForm: React.FC<BenefitFormProps> = ({ form }) => {
  // 监听权益类型变化
  const benefitType = Form.useWatch('type', form);

  // 获取权益类型列表
  const { data: benefitTypeList, isLoading: isLoadingBenefitTypes } = useQuery({
    queryKey: [QUERY_KEYS.BENEFIT_TYPE_LIST],
    queryFn: getBenefitTypeList,
  });

  // 获取商品列表
  const { data: productListData, isLoading: isLoadingProducts } = useQuery({
    queryKey: [QUERY_KEYS.PRODUCT_STOCK_LIST],
    queryFn: () =>
      getProductStockList({
        currentPage: 1,
        pageSize: QUERY_PAGE_SIZE,
      }),
  });

  // 过滤头像框数据
  const avatarFrameOptions = useMemo(() => {
    const records = extractProductRecords(productListData);
    return filterAvatarFrameOptions(records);
  }, [productListData]);

  // 根据当前选择的类型ID，获取对应的类型选项
  const currentTypeOption = useMemo(() => {
    if (!benefitType || !benefitTypeList) return null;
    return benefitTypeList.find((option) => option.id === benefitType);
  }, [benefitType, benefitTypeList]);

  // 根据limitStatus渲染不同的输入组件
  const renderTypeLimitInput = () => {
    if (!currentTypeOption || currentTypeOption.limitStatus === undefined) {
      return null;
    }

    const { limitStatus, limitDesc } = currentTypeOption;
    const label = limitDesc || '类型限制';
    const placeholder = `请输入${label}`;

    if (limitStatus === 2) {
      // limitStatus === 2: 显示会员昵称效果（ColorPicker）
      return (
        <Form.Item noStyle shouldUpdate>
          {() => {
            const gradientValue = form.getFieldValue('typeLimit');

            // 不能使用 useMemo，直接计算值
            let pickerValue: { color: string; percent: number }[];
            if (!gradientValue || gradientValue === '') {
              pickerValue = getDefaultGradient();
            } else if (typeof gradientValue === 'string' && gradientValue.includes('linear-gradient')) {
              // 已经是 linear-gradient 格式，直接解析
              const parsed = parseGradientString(gradientValue);
              pickerValue = parsed || getDefaultGradient();
            } else if (typeof gradientValue === 'string' && isColorPairFormat(gradientValue)) {
              // 如果是 #color1,#color2 格式，转换为 linear-gradient 格式后再解析
              const gradientString = convertColorPairToGradient(gradientValue);
              if (gradientString) {
                const parsed = parseGradientString(gradientString);
                pickerValue = parsed || getDefaultGradient();
              } else {
                pickerValue = getDefaultGradient();
              }
            } else if (Array.isArray(gradientValue)) {
              pickerValue = gradientValue;
            } else {
              pickerValue = getDefaultGradient();
            }

            // 限制为两种颜色：只保留第一个和最后一个颜色点
            const limitedPickerValue = limitGradientToTwoColors(pickerValue);

            return (
              <ColorPicker
                key={`color-picker-${benefitType}`}
                value={limitedPickerValue}
                allowClear
                showText
                mode="gradient"
                onChange={(color: Color) => {
                  if (color) {
                    const gradientString = formatGradientToHexLimited(color);
                    form.setFieldValue('typeLimit', gradientString);
                  } else {
                    form.setFieldValue('typeLimit', '');
                  }
                }}
              />
            );
          }}
        </Form.Item>
      );
    }

    if (limitStatus === 1) {
      // 部分类型需要支持文本输入（如 "30+", "All"），用 Input 替代 InputNumber
      const textInputTypes = [17]; // 17=可用风格
      if (benefitType && textInputTypes.includes(benefitType)) {
        return <Input placeholder={placeholder} maxLength={50} />;
      }
      // limitStatus === 1: 显示InputNumber
      return (
        <InputNumber
          placeholder={placeholder}
          min={0}
          style={{ width: '100%' }}
        />
      );
    }

    // 其他情况返回null，或者可以在这里添加更多扩展
    return null;
  };

  return (
    <Form form={form} layout="vertical" initialValues={FORM_DEFAULT_VALUES}>
      <Form.Item
        name="iconUrl"
        label="会员权益图标"
        rules={[{ required: true, message: '请上传会员权益图标' }]}
        extra="请上传会员权益图标，支持：jpg、jpeg、png、webp、gif、svga、lottie（.lottie/.json）"
      >
        <UploadImage
          width={UPLOAD_IMAGE_SIZE}
          height={UPLOAD_IMAGE_SIZE}
          showSizeHint={false}
          allowLottieSvga
          fileTypes={[
            'image/jpeg',
            'image/jpg',
            'image/png',
            'image/webp',
            'image/gif',
            '.svga',
          ]}
        />
      </Form.Item>

      <Form.Item
        name="titleEn"
        label="会员权益名称（英文）"
        rules={[{ required: true, message: '会员权益名称（英文' }]}
      >
        <Input placeholder="会员权益名称（英文）" />
      </Form.Item>

      <Form.Item
        name="titleJa"
        label="会员权益名称（日文）"
        rules={[{ required: true, message: '会员权益名称（日文' }]}
      >
        <Input placeholder="会员权益名称（日文）" />
      </Form.Item>

      <Form.Item
        name="obtainContentEn"
        label="权益获取文案（英文）"
        rules={[{ required: true, message: '权益获取文案（英文）' }]}
      >
        <Input placeholder="权益获取文案（英文）" />
      </Form.Item>

      <Form.Item
        name="obtainContentJa"
        label="权益获取文案（日文）"
        rules={[{ required: true, message: '权益获取文案（日文）' }]}
      >
        <Input placeholder="权益获取文案（日文）" />
      </Form.Item>

      <Form.Item
        name="type"
        label="会员权益类型"
        rules={[{ required: true, message: '请选择会员权益类型' }]}
      >
        <Select 
          placeholder="请选择会员权益类型"
          loading={isLoadingBenefitTypes}
          onChange={(value) => {
            // 切换类型时，如果不是对应的类型，清空 typeLimit
            // 这个逻辑在 BenefitDrawer 中处理
          }}
        >
          {isLoadingBenefitTypes ? (
            <Select.Option value="" disabled>加载中...</Select.Option>
          ) : benefitTypeList && benefitTypeList.length > 0 ? (
            benefitTypeList.map((typeOption) => (
              <Select.Option key={typeOption.id} value={typeOption.id}>
                {typeOption.name}
              </Select.Option>
            ))
          ) : (
            <Select.Option value="" disabled>暂无数据</Select.Option>
          )}
        </Select>
      </Form.Item>

      {/* 头像框类型：显示头像框选择器 */}
      {/* {benefitType === BenefitType.AVATAR_FRAME && (
        <>
          <Form.Item
            name="benefitAvatarFram"
            label="会员头像框（图片选择）"
            rules={[{ required: true, message: '请选择会员头像框' }]}
          >
            <AvatarFrameSelector options={avatarFrameOptions} />
          </Form.Item>

          <Form.Item
            name="benefitAvatar"
            label="会员头像框（下拉选择）"
            rules={[{ required: false, message: '请选择会员头像框' }]}
          >
            <AvatarFrameSelect
              options={avatarFrameOptions}
              loading={isLoadingProducts}
            />
          </Form.Item>
        </>
      )} */}

      {/* 根据接口返回的limitStatus动态渲染typeLimit字段 */}
      {currentTypeOption && currentTypeOption.limitStatus !== undefined && currentTypeOption.limitStatus !== 0 && (
        <Form.Item
          name="typeLimit"
          label={currentTypeOption.limitDesc || '类型限制'}
          rules={[{ required: benefitType !== BenefitType.SPEED_BOOST && benefitType !== 10, message: `请输入${currentTypeOption.limitDesc || '类型限制'}` }]}
        >
          {renderTypeLimitInput()}
        </Form.Item>
      )}

      {/* 昵称类型：显示会员点赞动效 */}
      {/* {benefitType === BenefitType.NICKNAME && (
        <Form.Item
          name="likeAnimation"
          label="会员点赞动效"
          rules={[{ required: true, message: '请上传会员点赞动效' }]}
          extra="请上传会员点赞动效，支持：jpg、jpeg、png、webp、gif、svga、lottie（.lottie/.json）"
        >
          <UploadImage
            width={UPLOAD_IMAGE_SIZE}
            height={UPLOAD_IMAGE_SIZE}
            showSizeHint={false}
            allowLottieSvga
            fileTypes={[
              'image/jpeg',
              'image/jpg',
              'image/png',
              'image/webp',
              'image/gif',
              '.svga',
            ]}
          />
        </Form.Item>
      )} */}

      {/* 免费生图类型：显示免费生图张数 */}
      {/* {benefitType === BenefitType.FREE_GENERATION && (
        <Form.Item
          name="typeLimit"
          label="免费生图张数"
          rules={[{ required: true, message: '请输入免费生图张数' }]}
        >
          <InputNumber
            placeholder="请输入免费生图张数"
            min={0}
            style={{ width: '100%' }}
          />
        </Form.Item>
      )} */}

      {/* 头像框类型：显示头像框地址 */}
      {/* {benefitType === BenefitType.AVATAR_FRAME && (
        <Form.Item
          name="typeLimit"
          label="头像框地址"
          rules={[{ required: true, message: '请输入头像框地址' }]}
        >
          <Input placeholder="请输入头像框地址" maxLength={500} />
        </Form.Item>
      )} */}

      {/* 图片高清下载类型：显示赠币/无水印下载数量 */}
      {/* {benefitType === BenefitType.HD_DOWNLOAD && (
        <Form.Item
          name="typeLimit"
          label="赠币/无水印下载数量"
          rules={[{ required: true, message: '请输入赠币/无水印下载数量' }]}
        >
          <InputNumber
            placeholder="请输入数量"
            min={0}
            style={{ width: '100%' }}
          />
        </Form.Item>
      )} */}

      {/* 作品曝光类型：显示曝光提升 */}
      {/* {benefitType === BenefitType.EXPOSURE && (
        <Form.Item
          name="typeLimit"
          label="曝光提升"
          rules={[{ required: true, message: '请输入曝光提升' }]}
        >
          <InputNumber
            placeholder="请输入曝光提升"
            min={0}
            style={{ width: '100%' }}
          />
        </Form.Item>
      )} */}

      {/* 速度提升类型：显示速度提升 */}
      {/* {benefitType === BenefitType.SPEED_BOOST && (
        <Form.Item
          name="typeLimit"
          label="速度提升"
          rules={[{ required: true, message: '请输入速度提升' }]}
        >
          <InputNumber
            placeholder="请输入速度提升"
            min={0}
            style={{ width: '100%' }}
          />
        </Form.Item>
      )} */}

      {/* 流量包类型：显示流量包数量配置 */}
      {/* {benefitType === BenefitType.DATA_PACKAGE && (
        <Form.Item
          name="typeLimit"
          label="流量包数量配置"
          rules={[{ required: true, message: '请输入流量包数量配置' }]}
        >
          <InputNumber
            placeholder="请输入流量包数量配置"
            min={0}
            style={{ width: '100%' }}
          />
        </Form.Item>
      )} */}

      {/* 专属风格包类型 */}
      {/* {benefitType === BenefitType.EXCLUSIVE_STYLE && (
        <Form.Item
          name="typeLimit"
          label="专属风格包"
          rules={[{ required: false, message: '专属风格包（可以不填）' }]}
        >
          <Input placeholder="请输入专属风格包" maxLength={500} />
        </Form.Item>
      )} */}

      {/* 多图输出类型 */}
      {/* {benefitType === BenefitType.MULTI_IMAGE && (
        <Form.Item
          name="typeLimit"
          label="多图输出"
          rules={[{ required: true, message: '请输入多图输出' }]}
        >
          <InputNumber
            placeholder="请输入多图输出"
            min={0}
            style={{ width: '100%' }}
          />
        </Form.Item>
      )} */}

      {/* 其它类型 */}
      {/* {benefitType === BenefitType.OTHER && (
        <Form.Item
          name="typeLimit"
          label="其它"
          rules={[{ required: false, message: '请输入其它' }]}
        >
          <Input placeholder="请输入其它" maxLength={500} />
        </Form.Item>
      )} */}

      <Form.Item
        name="groupKey"
        label="权益分组标识（E15对比表）"
        extra="同类权益使用相同的key（如 monthly_coins），用于对比表横向对齐"
      >
        <Input placeholder="请输入分组标识，如 monthly_coins、style_count" maxLength={50} />
      </Form.Item>

      <Form.Item
        name="groupCategory"
        label="权益大类（E15对比表）"
        extra="用于对比表分组显示"
      >
        <Select placeholder="请选择权益大类" allowClear>
          <Select.Option value="coins">coins - 金币権益</Select.Option>
          <Select.Option value="create">create - 創作権益</Select.Option>
          <Select.Option value="trade">trade - 取引権益</Select.Option>
          <Select.Option value="exclusive">exclusive - 専属権益</Select.Option>
        </Select>
      </Form.Item>

      <Form.Item
        name="corePrivilege"
        label="核心权益（订阅页展示）"
        extra="勾选后该权益将在订阅页展示"
      >
        <Radio.Group>
          <Radio value={0}>否</Radio>
          <Radio value={1}>是</Radio>
        </Radio.Group>
      </Form.Item>

      <Form.Item
        name="link"
        label="跳转页面"
        rules={[{ required: true, message: '请输入跳转页面' }]}
      >
        <Input placeholder="请输入跳转页面" maxLength={50} />
      </Form.Item>

      <Form.Item
        label="排序值"
        name="showOrder"
        rules={[{ required: true, message: '请输入排序值' }]}
      >
        <InputNumber placeholder="请输入排序值" style={{ width: '100%' }} />
      </Form.Item>

      <Form.Item
        name="status"
        label="启用状态"
        rules={[{ required: true, message: '请选择启用状态' }]}
      >
        <Radio.Group>
          <Radio value={0}>禁用</Radio>
          <Radio value={1}>启用</Radio>
        </Radio.Group>
      </Form.Item>
    </Form>
  );
};

export default BenefitForm;
